import { useAppDispatch, useAppSelector } from "@/app/store/hooks";
import { selectAllProjects } from "@/entities/projects/model/selects";
import type { ProjectsType } from "@/entities/projects/model/types";
import { selectMapFilterData } from "@/features/map_filter/model/selects";
import { resetFilterData } from "@/features/map_filter/model/slice";
import { MapFilter } from "@/features/map_filter/ui";
import { SectionLayout } from "@/shared/ui/layouts/SectionLayout";
import Typography from "@/shared/ui/Typography";
import { Map } from "@/widgets/map/ui/Map";
import { useMemo } from "react";
import { ProjectsListSection } from "./ProjectsListSection";
import { Search } from "@/features/search/ui/Search";
import { selectSearchText } from "@/features/search/model/selectors";
import { updateSearchText } from "@/features/search/model/slice";
import { selectDistricts } from "@/entities/site_data/model/selects";

export function MapFilterSection() {
    const projects = useAppSelector(selectAllProjects);
    const districts = useAppSelector(selectDistricts);

    const filterData = useAppSelector(selectMapFilterData);
    const searchText = useAppSelector(selectSearchText);
    const dispatch = useAppDispatch();

    const filteredData: ProjectsType = useMemo(() => {
        if (!projects) return [];

        const { apartment, price, districtId } = filterData;
        const search = searchText.trim().toLowerCase();

        const matchingDistrictIds = new Set(
            districts
                ?.filter((dist) => dist.name.toLowerCase().includes(search))
                .map((dist) => dist.id) || []
        );

        return projects.filter((project) => {
            if (apartment !== null && !project.rooms.includes(apartment)) {
                return false;
            }

            if (price !== null && project.priceFrom < price) {
                return false;
            }

            if (
                districtId !== null &&
                project.location.districtId !== districtId
            ) {
                return false;
            }

            if (search) {
                const matchesTitle = project.title
                    .toLowerCase()
                    .includes(search);
                const matchesSubtitle = project.subtitle
                    .toLowerCase()
                    .includes(search);
                const matchesDistrict = matchingDistrictIds.has(
                    project.location.districtId
                );

                if (!matchesTitle && !matchesSubtitle && !matchesDistrict) {
                    return false;
                }
            }

            return true;
        });
    }, [projects, districts, filterData, searchText]);

    const handleResetFilter = () => {
        dispatch(resetFilterData());
        dispatch(updateSearchText(""));
    };

    return (
        <SectionLayout
            className=""
            containerClassName="relative flex flex-col gap-[20px] md:gap-[40px] xl:gap-[52px] pt-[20px] md:pt-[40px] xl:pt-[52px] pb-[40px]"
        >
            <div className="relative flex flex-col gap-[16px] md:gap-[24px] xl:gap-[28px]">
                <Search />

                <div className="w-full flex flex-col items-center gap-[20px] lg:flex-row">
                    <MapFilter />

                    <button 
                        className="bg-white-50 text-black w-fit whitespace-nowrap"
                        onClick={handleResetFilter}
                    >
                        <Typography
                            className="underline text-[14px] md:text-[14px]"
                        >
                            Сбросить фильтры
                        </Typography>
                    </button>
                </div>

                <Map projects={filteredData || []} />
            </div>

            <ProjectsListSection projects={filteredData} />
        </SectionLayout>
    );
}
